import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useAdminAreaById } from "@/api/adminArea/userAdminAreaAPI";
import { useCountryQuery } from "@/api/country/useCountryAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon } from "@/assets/icons";
import AdminAreaSelection from "@/components/adminAreaSelection";
import {
  generateAttributeFilterOptions,
  getValueOfHighestLevelAdminArea,
  handleAdminAreaChangeHelper
} from "@/helpers/datasetAttributeHelper";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { updateFilters } from "@/store/slice/datasetSlice";
import {
  applyAdminTreeGlobalFilter,
  setAdminAreaSettings as setGlobalFilterAdminAreaSettings,
  setAdminAreaSettingsData as setGlobalFilterAdminAreaSettingsData,
  setAdminAreaSettingsDataTemp as setGlobalFilterAdminAreaSettingsDataTemp,
  setExplicitSelectedAreaIdsTemp,
  setSelectedAreaIdsTemp
} from "@/store/slice/globalFilterSlice";
import {
  setAdminAreaAddress,
  setAdminAreaSettingsData,
  setAdminAreaSettingsDataTemp,
  setAdminTreeSettingsExplicitSelectedIds,
  setAdminTreeSettingsSelectedIds,
  setAdminTreeSettingsTempExplicitSelectedIds,
  setAdminTreeSettingsTempSelectedIds,
  setSpatialConfig
} from "@/store/slice/spatialSlice";
import { AdminAreaData } from "@/types/adminArea";
import { AdminAreaSettings, AreaInputType } from "@/types/spatial";

import { area } from "@turf/turf";
import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "admin-area-settings-popup";

interface Props {
  onClose: () => void;
  type?: Extract<keyof RootState, "spatial" | "globalFilter">;
}

const AdminAreaSettingsPopup = ({ onClose, type = "spatial" }: Props) => {
  const dispatch = useDispatch();
  const { isAdminTreeEnabled } = useAppFeatures();
  const { clearLoadingState, showLoading } = useLoadingAndToast();
  const { datasets, filters } = useSelector(
    (state: RootState) => state.dataset
  );
  const {
    adminAreaSettings,
    adminAreaSettingsDataTemp,
    spatialConfig,
    adminTreeSettingsTemp,
    overrideGlobalFilter
  } = useSelector((state: RootState) => state.spatial);
  const {
    linkedDatasetIds,
    selectedAreaIdsTemp,
    explicitSelectedAreaIdsTemp,
    adminAreaSettings: globalAdminAreaSettings,
    adminAreaSettingsDataTemp: globalAdminAreaSettingsDataTemp
  } = useSelector((state: RootState) => state.globalFilter);
  const isGlobal = type === "globalFilter";

  const [localAdmAreaSetting, setLocalAdmAreaSetting] =
    useState<AdminAreaSettings | null>(
      isGlobal ? globalAdminAreaSettings : adminAreaSettings
    );

  const selectedIds = isGlobal
    ? selectedAreaIdsTemp
    : (adminTreeSettingsTemp?.selectedAreaIds ?? []);
  const explicitSelectedIds = isGlobal
    ? explicitSelectedAreaIdsTemp
    : (adminTreeSettingsTemp?.explicitSelectedAreaIds ?? []);

  const [firstLoad, setFirstLoad] = useState(true);

  const { zoomLevel, fitMapToLayerBound } = useMap();
  const [adminAreaId, setAdminAreaId] = useState<string | null>(
    getValueOfHighestLevelAdminArea(localAdmAreaSetting)
  );

  const { data: adminAreaDataById = null } = useAdminAreaById(
    adminAreaId,
    zoomLevel,
    { enabled: !firstLoad && !!adminAreaId }
  );

  const { data: lv0Data } = useCountryQuery(localAdmAreaSetting?.lv0);

  const handleAdminAreaChange = (admAreaSetting: AdminAreaSettings | null) => {
    setLocalAdmAreaSetting(admAreaSetting);
    const adminAreaIdHighest = getValueOfHighestLevelAdminArea(admAreaSetting);
    if (!adminAreaIdHighest) return;
    setAdminAreaId(adminAreaIdHighest);
  };

  const resetResolution = (areaSize: number) => {
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        areaSize,
        gridResolution: null,
        h3Resolution: null
      })
    );
  };

  const handleTreeAdminAreaInput = () => {
    dispatch(setAdminTreeSettingsSelectedIds(selectedIds));
    dispatch(setAdminTreeSettingsExplicitSelectedIds(explicitSelectedIds));
    dispatch(setAdminTreeSettingsTempSelectedIds([]));
    dispatch(setAdminTreeSettingsTempExplicitSelectedIds([]));
  };

  const { data: userProfile } = useGetUserProfileAPI();
  const { handleUpdateDatasetData, generateDatasetData } = useDataset();

  const onSaveSetting = () => {
    const isTree = isAdminTreeEnabled;
    const needApplyToAreaInput =
      !overrideGlobalFilter &&
      spatialConfig.areaInput?.value === AreaInputType.ADMIN_AREA;

    const needApplyToDatasetsFilter = linkedDatasetIds.length > 0;

    // normalize per-mode data & actions
    const config = isGlobal
      ? {
          setSettings: async (settings: AdminAreaSettings | null) => {
            dispatch(setGlobalFilterAdminAreaSettings(settings));
            if (needApplyToAreaInput) dispatch(setAdminAreaAddress(settings));
            if (needApplyToDatasetsFilter) {
              showLoading("Updating datasets...", true);
              for (const datasetId of linkedDatasetIds) {
                const dataset = datasets[datasetId];
                const savedFilters = filters[datasetId] || {};

                const newFilter = handleAdminAreaChangeHelper(
                  settings,
                  generateAttributeFilterOptions(dataset.data),
                  savedFilters
                );
                const currentDatasetData = await fetchDatasetStream({
                  datasetId: datasetId.split("_")[0],
                  filters: newFilter,
                  h3Configuration: dataset.h3Configuration,
                  timezone: userProfile?.timezone,
                  generateDatasetData
                });
                if (currentDatasetData.rawDataset) {
                  handleUpdateDatasetData(
                    datasetId,
                    currentDatasetData.rawDataset,
                    dataset.h3Configuration
                  );
                }
                dispatch(
                  updateFilters({
                    datasetId,
                    filters: newFilter
                  })
                );
              }
              clearLoadingState();
            }
          },
          dataTemp: globalAdminAreaSettingsDataTemp,
          setData: (data: AdminAreaData | null) => {
            dispatch(setGlobalFilterAdminAreaSettingsData(data));
            if (needApplyToAreaInput) dispatch(setAdminAreaSettingsData(data));
          },
          afterTreeAction: () => {
            dispatch(applyAdminTreeGlobalFilter());
            if (needApplyToAreaInput) {
              handleTreeAdminAreaInput();
            }
          }
        }
      : {
          setSettings: (settings: AdminAreaSettings | null) => {
            dispatch(setAdminAreaAddress(settings));
          },
          dataTemp: adminAreaSettingsDataTemp,
          setData: (data: AdminAreaData | null) => {
            dispatch(setAdminAreaSettingsData(data));
          },
          afterTreeAction: () => {
            handleTreeAdminAreaInput();
          }
        };

    // always save base settings
    config.setSettings(localAdmAreaSetting);

    if (isTree) {
      // tree mode
      config.afterTreeAction();
    } else {
      // non-tree mode (compute boundary area)
      if (!config.dataTemp?.data?.length) return;
      const boundary = config.dataTemp.data[0].boundary;
      const areaSize = area(boundary);

      config.setData(config.dataTemp);
      resetResolution(areaSize);
    }

    onClose();
  };

  const handleAdminAreaDataChange = useCallback(
    (data: AdminAreaData | null) => {
      if (isGlobal) {
        dispatch(setGlobalFilterAdminAreaSettingsDataTemp(data));
      } else {
        dispatch(setAdminAreaSettingsDataTemp(data));
      }
    },
    [isGlobal, dispatch]
  );

  const handleSetDataPolygon = useCallback(() => {
    // Case user not select any country
    if (!localAdmAreaSetting?.lv0) {
      handleAdminAreaDataChange(null);
      return;
    }
    // Case user select country but not select any admin area
    if (!localAdmAreaSetting?.lv1) {
      handleAdminAreaDataChange(lv0Data ?? null);
      if (lv0Data) {
        fitMapToLayerBound(lv0Data);
      }
      return;
    }
    // Case cover admin area from level 2 to level 5
    handleAdminAreaDataChange(adminAreaDataById ?? null);
    if (adminAreaDataById) {
      fitMapToLayerBound(adminAreaDataById);
    }
  }, [
    localAdmAreaSetting,
    lv0Data,
    adminAreaDataById,
    fitMapToLayerBound,
    handleAdminAreaDataChange
  ]);

  useEffect(() => {
    // Check if it's the first load, will ignore
    if (firstLoad) {
      setFirstLoad(false);
      return;
    }
    handleSetDataPolygon();
  }, [handleSetDataPolygon, firstLoad]);

  const adminTreeConfig = {
    enabled: isAdminTreeEnabled,

    selectedIds,
    explicitSelectedIds,

    setSelectedIds: (ids: string[]) => {
      if (isGlobal) {
        dispatch(setSelectedAreaIdsTemp(ids));
      } else {
        dispatch(setAdminTreeSettingsTempSelectedIds(ids));
      }
    },

    setExplicitSelectedIds: (ids: string[]) => {
      if (isGlobal) {
        dispatch(setExplicitSelectedAreaIdsTemp(ids));
      } else {
        dispatch(setAdminTreeSettingsTempExplicitSelectedIds(ids));
      }
    }
  };

  return (
    <div
      id="admin-area-settings"
      className={twMerge(
        "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px]",
        "flex flex-col p-[16px] absolute left-[370px] top-[30px]",
        "max-h-[calc(100vh-54px)]"
      )}
    >
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-row items-center gap-2">
          <h2 className="text-txtHeading font-bold text-[20px]">
            Administrative Area
          </h2>
        </div>
        <button
          className="bg-transparent border-0 p-0"
          onClick={onClose}
          data-testid="btn-close-adminArea"
          aria-label="Close"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>
      {/* Content */}
      <div className="flex flex-col mt-2">
        <p className="text-txtBody text-[10px]">
          Countries have varying levels of administrative areas.
        </p>
        <AdminAreaSelection
          selectedData={localAdmAreaSetting}
          onSelectData={handleAdminAreaChange}
          treeConfig={adminTreeConfig}
          options={{
            showCountryLevel: true,
            showLabelAboveInput: true,
            showLabel: false,
            prefixLabel: "Search",
            required: {
              lv0: true,
              lv1: false,
              lv2: false,
              lv3: false,
              lv4: false
            }
          }}
        />
        <Button
          className={twMerge(
            "h-[40px] mt-3 border bg-teal-500 text-white text-sm font-bold",
            "disabled:border-none disabled:bg-gainsboro disabled:text-white"
          )}
          disabled={!localAdmAreaSetting?.lv1 && selectedIds.length === 0}
          onClick={onSaveSetting}
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          <span>Save</span>
        </Button>
      </div>
    </div>
  );
};

export default AdminAreaSettingsPopup;
